import { useEffect, useMemo, useState } from 'react';
import { assetsMap } from '@flobi/artwork-core';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { Spinner } from '../../ui/controls';
import { tip } from '../../ui/tooltip';
import { tileUrl } from '../content/tileUrl';
import apiClient from '../../services/apiClient';
import type { OpenDocument } from '../useEditorDocument';
import { pictureLayer } from '../imageTools/run';
import { promptReadable } from './promptMentions';
import type { Binding, GenerationClient, MediaKind, Recipe, Reference } from './contracts';
import './generationInfo.css';

/*
 * Info, the side panel's third tab on a picture or clip (Tarek, 15 Oct: "for generated content can
 * I have asset info … prompt used, references used, model, settings, similar to the Playground —
 * when I press on them they get added to a new generation job in the Generate tab so I can reuse
 * them"). The picture's own facts for anything; for a generated one, how it was made — each part a
 * button that puts it in Generate, and Use All for the whole recipe (the Playground's Recreate).
 *
 * Where the recipe comes from: a run made in this file is in the file (`artworkAiRuns`), whole —
 * references with their media ids, the settings as sent. Anything else (placed from My Stuff) is
 * asked of nodes-back's `getRecipesByAssetIds`, which knows the Playground run that made it.
 */

interface Made {
  kind: MediaKind;
  prompt: string;
  model: string;
  inputs: Record<string, unknown>;
  references: Reference[];
  video?: { side: 'frames' | 'references' };
  at?: number;
  /** A picture tool's run (Remove Background, Upscale…): said, not offered for reuse. */
  tool?: string;
}

/** The settings worth showing, in the order a person reads them, and how each is said. */
const SETTINGS: { key: string; also?: string; label: (value: unknown) => string | null }[] = [
  { key: 'aspectRatio', also: 'aspect_ratio', label: value => (value ? String(value) : null) },
  { key: 'customSize', label: value => { const size = value as { width?: number; height?: number } | null; return size?.width && size.height ? `${size.width} × ${size.height}` : null; } },
  { key: 'quality', label: value => (value ? String(value) : null) },
  { key: 'duration', label: value => (value ? `${String(value).replace(/s$/, '')} s` : null) },
  { key: 'mode', label: value => (value ? `${String(value)[0]!.toUpperCase()}${String(value).slice(1)}` : null) },
  { key: 'generate_audio', label: value => (value === true ? 'Sound' : null) },
  { key: 'transparentBackground', label: value => (value === true ? 'Transparent' : null) },
  { key: 'thinkingLevel', label: value => (value ? `Thinking ${String(value)}` : null) },
];

function madeInFile(open: OpenDocument, assetId: string): Made | null {
  let found: Binding | null = null;
  for (const run of open.root.getMap<Binding>('artworkAiRuns').values()) {
    if (run.results?.some(result => result.mediaAssetId === assetId) && (!found || run.createdAt > found.createdAt)) found = run;
  }
  if (!found) return null;
  const request = found.request;
  return { kind: request.kind, prompt: request.prompt, model: request.model, inputs: request.inputs ?? {}, references: request.references ?? [], video: request.video, at: found.createdAt, tool: found.tool?.label };
}

/** A Playground run's recipe, by the asset it made (nodes-back `getRecipesByAssetIds`). */
async function madeElsewhere(mediaAssetId: string, kind: MediaKind): Promise<Made | null> {
  const response = await apiClient.post('/nodes/nodes.generations.getRecipesByAssetIds', { mediaAssetIds: [mediaAssetId] });
  const body = response.data?.data ?? response.data;
  const recipe = body?.[mediaAssetId]?.recipe as { model?: string | null; prompt?: string | null; settings?: Record<string, unknown>; inputMedia?: { type: string; url: string; mediaAssetId?: string | null }[] } | undefined;
  if (!recipe?.model && !recipe?.prompt) return null;
  const { images_urls: _images, videoUrl: _video, video_url: _video2, audioUrl: _audio, audio_url: _audio2, prompt: _prompt, ...inputs } = recipe.settings ?? {};
  const references: Reference[] = (recipe.inputMedia ?? []).map((media, index) => ({
    id: `recipe-${index}`, name: `Reference ${index + 1}`, url: media.url, mime: media.type === 'video' ? 'video/mp4' : media.type === 'audio' ? 'audio/mpeg' : 'image/png',
    width: 0, height: 0, origin: 'upload' as const, ...(media.mediaAssetId ? { mediaAssetId: media.mediaAssetId } : {}),
    ...(media.type !== 'image' ? { mediaKind: media.type as 'video' | 'audio', role: media.type as 'video' | 'audio' } : {}),
  }));
  return { kind, prompt: recipe.prompt ?? '', model: recipe.model ?? '', inputs, references };
}

export function GenerationInfo({ id, open, client, onUse }: { id: string; open: OpenDocument; client: GenerationClient; onUse(recipe: Recipe): void }) {
  const layer = pictureLayer(open, id);
  const asset = layer ? (assetsMap(open.root).get(layer.assetId) as { name?: string; mime?: string; width?: number; height?: number; kind?: string; src?: string; mediaAssetId?: string; duration?: number } | undefined) : undefined;
  const kind: MediaKind = asset?.kind === 'video' || layer?.clip ? 'video' : 'image';
  const inFile = useMemo(() => (layer ? madeInFile(open, layer.assetId) : null), [open, layer?.assetId]);
  const [remote, setRemote] = useState<{ assetId: string; made: Made | null } | null>(null);
  const [modelNames, setModelNames] = useState<Map<string, string>>(new Map());
  const [expanded, setExpanded] = useState(false);
  const [used, setUsed] = useState('');

  // Not made in this file: ask what made it, once per picture.
  const mediaAssetId = asset?.mediaAssetId ?? layer?.assetId;
  useEffect(() => {
    if (inFile || !mediaAssetId) return;
    let live = true;
    madeElsewhere(mediaAssetId, kind).then(made => { if (live) setRemote({ assetId: mediaAssetId, made }); }).catch(() => { if (live) setRemote({ assetId: mediaAssetId, made: null }); });
    return () => { live = false; };
  }, [inFile, mediaAssetId, kind]);
  useEffect(() => { setExpanded(false); setUsed(''); }, [id]);
  const made = inFile ?? (remote && remote.assetId === mediaAssetId ? remote.made : null);
  const asking = !inFile && remote?.assetId !== mediaAssetId;

  // The model by the name Generate shows it under.
  useEffect(() => {
    if (!made?.model) return;
    let live = true;
    Promise.all([client.models(made.kind, false), client.models(made.kind, true)]).then(lists => {
      if (live) setModelNames(new Map(lists.flat().map(model => [model.id, model.name])));
    }).catch(() => undefined);
    return () => { live = false; };
  }, [client, made?.model, made?.kind]);

  if (!layer || !asset) return null;
  const use = (what: string, recipe: Recipe) => { onUse({ kind: made?.kind ?? kind, ...recipe }); setUsed(what); };
  const settings = made ? SETTINGS.flatMap(setting => {
    const value = made.inputs[setting.key] ?? (setting.also ? made.inputs[setting.also] : undefined);
    const label = setting.label(value);
    return label ? [{ key: setting.key, label, value }] : [];
  }) : [];
  const prompt = made ? promptReadable(made.prompt).trim() : '';
  const reusable = !!made && !made.tool;

  return <div className="generation-info">
    <dl className="generation-info-facts">
      {/* A result's file is named by its storage key ("standalone-b0eb…"): the layer's name says more. */}
      <div><dt>Name</dt><dd>{(asset.name && !/^standalone-/.test(asset.name) ? asset.name : layer.name) || 'Picture'}</dd></div>
      {asset.width && asset.height ? <div><dt>Size</dt><dd className="tnum">{asset.width} × {asset.height} px{asset.duration ? ` · ${Math.round(asset.duration)} s` : ''}</dd></div> : null}
      {/* Said only when known: no recipe found is not proof a picture was uploaded (a copy saved to My Stuff has none). */}
      {made ? <div><dt>Made</dt><dd>{made.tool ? `With ${made.tool}` : 'Generated'}{made.at ? ` · ${new Date(made.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</dd></div> : null}
    </dl>

    {asking ? <p className="generation-info-note"><Spinner size={12} />Finding how this was made…</p> : null}

    {made ? <>
      {prompt ? <section className="generation-info-group">
        <h4>Prompt</h4>
        {reusable ? <button type="button" className={`generation-info-prompt${expanded ? ' is-open' : ''}`} {...tip('Use this prompt in Generate')} onClick={() => use('Prompt', { prompt: made.prompt })}>{prompt}</button>
          : <p className={`generation-info-prompt${expanded ? ' is-open' : ''}`}>{prompt}</p>}
        {prompt.length > 220 ? <button type="button" className="generation-info-more" onClick={() => setExpanded(was => !was)}>{expanded ? 'Show Less' : 'Show All'}</button> : null}
      </section> : null}

      {made.references.length ? <section className="generation-info-group">
        <h4>References</h4>
        <div className="generation-info-refs">
          {made.references.map((ref, index) => reusable
            ? <button key={`${ref.id}:${index}`} type="button" className="generation-info-ref" aria-label={`Use ${ref.name} as a reference`} {...tip('Add as a reference in Generate')} onClick={() => use('Reference', { references: [ref] })}>
              {ref.mediaKind === 'audio' ? <small>Audio</small> : <img src={tileUrl(ref.thumbnailUrl || ref.url, 160)} alt="" draggable={false} />}
            </button>
            : <span key={`${ref.id}:${index}`} className="generation-info-ref"><img src={tileUrl(ref.thumbnailUrl || ref.url, 160)} alt="" draggable={false} /></span>)}
        </div>
      </section> : null}

      {made.model ? <section className="generation-info-group">
        <h4>Model</h4>
        {reusable ? <button type="button" className="generation-info-chip is-wide" {...tip('Use this model in Generate')} onClick={() => use('Model', { model: made.model })}>{modelNames.get(made.model) ?? made.model}</button>
          : <span className="generation-info-chip is-wide is-still">{modelNames.get(made.model) ?? made.model}</span>}
      </section> : null}

      {settings.length ? <section className="generation-info-group">
        <h4>Settings</h4>
        <div className="generation-info-chips">
          {settings.map(setting => reusable
            ? <button key={setting.key} type="button" className="generation-info-chip" {...tip('Use this setting in Generate')} onClick={() => use(setting.label, { inputs: { [setting.key]: setting.value } })}>{setting.label}</button>
            : <span key={setting.key} className="generation-info-chip is-still">{setting.label}</span>)}
        </div>
      </section> : null}

      {reusable ? <div className="generation-info-go">
        <button type="button" className="image-tools-primary" onClick={() => use('Everything', { prompt: made.prompt, model: made.model, inputs: made.inputs, references: made.references, video: made.video })}>
          <Icon name="generate-sparkles" size={ICON_SIZE.small} /><span>Use All in Generate</span>
        </button>
        <p className="image-tools-price" aria-live="polite">{used ? `${used} added to Generate.` : 'Or press any part above to use it alone.'}</p>
      </div> : null}
    </> : null}
  </div>;
}
